import {ErrorCode} from './vocabulary.js';

/**
 * Turns a failed request into the message a user sees. The backend answers with short
 * English texts, and a network failure surfaces as the browser's own wording, so the
 * dashboard shows a translated message instead: one of its own for a failure the backend
 * names in its error code, otherwise one per HTTP status. Only a validation, conflict or
 * processing text from the backend (400, 409, 422) says what the user can change, so that
 * text is kept and shown inside the translated sentence.
 *
 * The result is a translation key with its template values, or a literal text for
 * failures raised inside the dashboard itself (a rejected string, or an error without
 * an HTTP status), which already speak to the user. A programming error (such as a
 * `TypeError` that is not a failed request) gets the generic message, since its text
 * is meant for developers.
 */

/** @typedef {{key: string, vars?: Object<string, string>} | {text: string}} ErrorDescription */

/** Backend bodies that name no concrete problem, so they add nothing to a translated message. */
const GENERIC_BODIES = new Set(['invalid request', 'invalid multipart request']);

/**
 * @param {unknown} error
 * @returns {string} the backend's text, trimmed, or '' when it has none or only a generic one
 */
function specificDetail(error) {
    const message = typeof error?.message === 'string' ? error.message.trim() : '';
    return GENERIC_BODIES.has(message.toLowerCase()) ? '' : message;
}

/**
 * How browsers word a `fetch` that never got a response: Chrome and Edge ("Failed to fetch"),
 * Firefox ("NetworkError when attempting to fetch resource."), Safari ("Load failed") and
 * React Native and older polyfills ("Network request failed").
 */
const NETWORK_FAILURE_MESSAGE = /failed to fetch|networkerror|load failed|network request failed/i;

/** Built-in error types a bug in the dashboard raises; their text is not for users. */
const PROGRAMMING_ERRORS = [TypeError, ReferenceError, RangeError, SyntaxError];

/** Messages for failures the backend names in its error code, whatever their status. */
const CODE_MESSAGES = Object.freeze({
    [ErrorCode.NO_CHANGES]: 'errors.noChanges',
    [ErrorCode.NO_DRAFTS]: 'errors.noDrafts',
    [ErrorCode.HOMEPAGE_NOT_DELETABLE]: 'errors.homepageNotDeletable',
    [ErrorCode.PATH_TAKEN]: 'errors.pathTaken',
});

/**
 * @param {unknown} error
 * @returns {boolean} whether the request never reached the server: `fetch` rejects with a
 *     TypeError worded as a network failure, unlike a TypeError raised by a bug
 */
export function isNetworkError(error) {
    return error instanceof TypeError && NETWORK_FAILURE_MESSAGE.test(error.message);
}

/**
 * @param {unknown} error
 * @returns {boolean} whether the error is a built-in one raised by a bug rather than a failed request
 */
function isProgrammingError(error) {
    return PROGRAMMING_ERRORS.some((type) => error instanceof type);
}

/**
 * Describes a failed page, template or asset request.
 * @param {unknown} error a rejected value: an `ApiError`, a `TypeError` from `fetch`, a string or anything else
 * @returns {ErrorDescription}
 */
export function describeError(error) {
    if (typeof error === 'string' && error.trim()) {
        return {text: error};
    }
    if (isNetworkError(error)) {
        return {key: 'global.networkError'};
    }
    const status = typeof error?.status === 'number' ? error.status : null;
    if (status === null) {
        const message = typeof error?.message === 'string' ? error.message.trim() : '';
        return message && !isProgrammingError(error) ? {text: message} : {key: 'global.unexpectedError'};
    }
    const code = typeof error?.code === 'string' ? error.code : '';
    if (Object.hasOwn(CODE_MESSAGES, code)) {
        return {key: CODE_MESSAGES[code]};
    }
    const detail = specificDetail(error);
    if (code === ErrorCode.ASSET_UPLOAD_FAILURE) {
        return detail ? {key: 'errors.assetUploadDetail', vars: {detail}} : {key: 'errors.assetUpload'};
    }
    switch (status) {
        case 400:
            return detail ? {key: 'errors.validation', vars: {detail}} : {key: 'errors.badRequest'};
        case 401:
            return {key: 'global.sessionExpired'};
        case 403:
            return {key: 'errors.forbidden'};
        case 404:
            return {key: 'errors.notFound'};
        case 409:
            return detail ? {key: 'errors.conflictDetail', vars: {detail}} : {key: 'errors.conflict'};
        case 413:
            return {key: 'errors.tooLarge'};
        case 422:
            return detail ? {key: 'errors.unprocessableDetail', vars: {detail}} : {key: 'errors.badRequest'};
        case 429:
            return {key: 'errors.rateLimited'};
        default:
            return {key: 'global.unexpectedError'};
    }
}

/**
 * Describes a failed login or registration. The auth endpoints use their statuses for
 * specific outcomes (wrong password, taken email, unknown organization, wrong code);
 * any other failure is described as for {@link describeError}.
 * @param {unknown} error
 * @returns {ErrorDescription}
 */
export function describeAuthError(error) {
    switch (error?.status) {
        case 401:
            return {key: 'errors.invalidCredentials'};
        case 403:
            return {key: 'errors.invalidRegistrationCode'};
        case 404:
            return {key: 'errors.organizationNotFound'};
        case 409:
            return {key: 'errors.emailTaken'};
        default:
            return describeError(error);
    }
}
